import { getIndicatorDefinition } from "./registry";
import { normalizeInputs, mergeStyles } from "./indicatorSettings";
import { createIndicatorInstance } from "./indicatorInstance";
import { mergeRanges } from "./valueRange";
import { readIndicatorValues } from "./indicatorValues";
import { createIndicatorList } from "./indicatorList";

const KEY_PREFIX = "indicator-";

/**
 * Indicators of the chart (engine.addIndicator & co.): the list in the
 * order they were added, the shown bars they are calculated on (animator)
 * and the event of list changes. Price indicators are drawn on the main
 * pane, each oscillator in its own pane (paneId = its key) under it.
 *
 * @param {object} params
 * @param {object} params.glLayer
 * @param {object} params.theme - engine theme: default colours of the styles
 * @param {() => void} params.invalidate - schedules a frame
 * @param {() => number} params.getPriceStep - price step of the pair (ZigZag deviation in points)
 */
export const createIndicators = ({ glLayer, theme, invalidate, getPriceStep }) => {
  const { instances, find, changed, describeAll, subscribe, clear } = createIndicatorList(invalidate);
  let bars = [];
  let nextNumber = 1;

  return {
    /**
     * @param {string} id - registry id, e.g. "moving-average"
     * @param {object} [inputs] - over the PO defaults
     * @param {object} [styles] - over the default styles (mergeStyles)
     * @returns {string|null} key of the added indicator; null for an unknown id
     */
    add: (id, inputs, styles) => {
      const definition = getIndicatorDefinition(id);

      if (!definition) return null;

      const instance = createIndicatorInstance({
        key: `${KEY_PREFIX}${nextNumber}`,
        definition,
        inputs: normalizeInputs(definition, inputs),
        styles: mergeStyles(definition, theme, null, styles),
        glLayer,
        getPriceStep,
      });

      nextNumber += 1;
      instance.setBars(bars, false);
      instances.push(instance);
      changed();

      return instance.key;
    },

    /** @returns {boolean} false for an unknown key */
    remove: (key) => {
      const instance = find(key);

      if (!instance) return false;

      instances.splice(instances.indexOf(instance), 1);
      instance.release();
      changed();

      return true;
    },

    /** Removes every indicator with one list event. */
    removeAll: () => {
      if (!instances.length) return;

      instances.forEach((instance) => instance.release());
      instances.length = 0;
      changed();
    },

    /**
     * @param {string} key
     * @param {{ inputs?: object, styles?: object }} changes - over the current ones
     * @returns {boolean} false for an unknown key
     */
    update: (key, { inputs, styles } = {}) => {
      const instance = find(key);

      if (!instance) return false;

      const { definition } = instance;

      if (inputs) instance.setInputs(normalizeInputs(definition, { ...instance.inputs, ...inputs }));
      if (styles) instance.setStyles(mergeStyles(definition, theme, instance.styles, styles));

      changed();

      return true;
    },

    /** @returns {boolean} false for an unknown key */
    setVisible: (key, isVisible) => {
      const instance = find(key);

      if (!instance) return false;

      instance.setVisible(Boolean(isVisible));
      changed();

      return true;
    },

    /** @returns {object[]} key, id, name, isOscillator, paneId, inputs, styles, isVisible, plotKeys */
    list: describeAll,

    /** @param {(indicators: object[]) => void} listener - gets list() at once and after every change */
    subscribe,

    /** @returns {object[]|null} values per bar, see readIndicatorValues; null for an unknown key */
    values: (key, storeBars) => {
      const instance = find(key);

      return instance ? readIndicatorValues(instance, storeBars) : null;
    },

    /** Shown bars as a whole (animator); a new bar on the right is calculated incrementally. */
    setBars: (nextBars, isNewBar) => {
      bars = nextBars;
      instances.forEach((instance) => instance.setBars(bars, isNewBar));
    },

    /** The last shown bar moved (a tick or a frame of its animation). */
    updateLastBar: (nextBars) => {
      bars = nextBars;
      instances.forEach((instance) => instance.updateLastBar(bars));
    },

    /** Indicators over the price chart (their signal labels, drawIndicatorLabels). */
    get priceIndicators() {
      return instances.filter((instance) => !instance.definition.isOscillator);
    },

    /** Oscillators, top to bottom: each gets a pane under the price chart. */
    get oscillators() {
      return instances.filter((instance) => instance.definition.isOscillator);
    },

    /** @returns {{ min: number, max: number } | null} values of the pane's indicators, bars first..last */
    priceRange: (paneId, first, last) =>
      mergeRanges(
        instances
          .filter((instance) => instance.paneId === paneId)
          .map((instance) => instance.priceRange(first, last)),
      ),

    /** @param {object} params - see indicatorRenderer.draw */
    draw: (paneId, params) => {
      instances.forEach((instance) => {
        if (instance.paneId === paneId) instance.draw(params);
      });
    },

    destroy: clear,
  };
};
